import {
  CheckIcon,
  Cross1Icon,
  EnvelopeClosedIcon,
  MinusIcon,
  PersonIcon,
  PlusIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFriendRequest from "src/lib/hooks/useFriendRequest";
import { Friend } from "src/lib/slices/userSlice";
import useSWR from "swr";
import Indicator from "./Indicator";
import Loading from "./Loading";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Separator } from "./ui/separator";
import UserAvatar from "./userAvatar";

const fetcher = (token) => (url: string) =>
  fetch(url, {
    headers: {
      Authorization: token,
    },
  }).then((res) => res.json());

export default function UserCard({
  userId,
  Content,
}: {
  userId: string;
  Content?: any;
}) {
  const user = useSelector((state: any) => state.user);

  const { data, error, isLoading, mutate } = useSWR<Friend>(
    `${process.env.REACT_APP_API_URL}/users/by-id/${userId}`,
    fetcher(user.token)
  );

  const [friendRequestLoading, setFriendRequestLoading] = useState(false);

  const { friendData, sendFriendRequest, removeFriend } = useFriendRequest({
    userData: data,
    setFriendRequestLoading,
    mutate,
  });

  const [removeFriendClicked, setRemoveFriendClicked] = useState(false);
  const handleRemoveFriend = () => {
    if (!removeFriendClicked) setRemoveFriendClicked(true);
    else {
      setRemoveFriendClicked(false);
      removeFriend();
    }
  };
  useEffect(() => {
    if (removeFriendClicked) {
      const timeout = setTimeout(() => setRemoveFriendClicked(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [removeFriendClicked]);

  if (error)
    return (
      <Card>
        <CardHeader>Někde se stala chyba, zkus to prosím později</CardHeader>
      </Card>
    );

  if (isLoading)
    return (
      <Card>
        <CardHeader>
          <Loading />
        </CardHeader>
      </Card>
    );

  return (
    <Card>
      <CardHeader
        className={`p-4 space-y-0 ${Content ? "pb-2" : "pb-4"}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1ch",
          flexDirection: "row",
          justifyContent: "space-between",
          maxWidth: "100%",
        }}
      >
        <Link
          to={`/user/${userId}`}
          style={{ height: "100%", flex: "1 1", maxWidth: "100%", minWidth: 0 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1ch",
              flexDirection: "row",
              maxWidth: "100%",
            }}
          >
            <UserAvatar friend={data} />
            <div
              style={{
                flex: "1 1",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  color: !data?.name
                    ? "hsl(var(--muted-foreground))"
                    : undefined,
                  margin: 0,
                  fontWeight: !data?.name ? 400 : 500,
                  textWrap: "wrap",
                  wordWrap: "break-word",
                  minWidth: 0,
                  display: "block",
                }}
                className="text-sm"
              >
                {data?.name ?? "Beze jména"}
              </span>
              {data?.tag && (
                <span
                  style={{
                    display: "block",
                    color: "hsl(var(--muted-foreground))",
                    textWrap: "wrap",
                    wordWrap: "break-word",
                    minWidth: 0,
                  }}
                  className="text-xs"
                >
                  @{data?.tag}
                </span>
              )}
            </div>
            {friendData?.isFriend && data?.isFriend && (
              <>
                <Separator
                  orientation="vertical"
                  style={{ height: "1.5rem", margin: 0 }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    margin: 0,
                  }}
                >
                  <div style={{ height: "1.5rem", margin: 0 }}>
                    <Indicator
                      doNotDisplayText
                      value={data.permille}
                      maxValue={data.maxPermille}
                    />
                  </div>
                  <div
                    className="m-0 text-muted-foreground text-sm flex flex-col items-center relative"
                    style={{ lineHeight: "0.875rem" }}
                  >
                    <span style={{ fontWeight: 500 }}>{data.permille}</span>
                    <div
                      style={{
                        fontSize: "0.5rem",
                        position: "absolute",
                        top: "0.75rem",
                      }}
                    >
                      ‰
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Link>
        {friendRequestLoading && (
          <Button
            size="icon"
            disabled
            variant="outline"
            style={{ flexShrink: "0" }}
          >
            <PersonIcon />
            <ReloadIcon />
          </Button>
        )}
        {!friendRequestLoading && (
          <>
            {friendData?.friendRequestReceived && (
              <>
                {friendData?.isFriend === false && (
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={removeFriend}
                    style={{ flexShrink: "0" }}
                  >
                    <PersonIcon />
                    <Cross1Icon />
                  </Button>
                )}
              </>
            )}
            {(!friendData?.friendRequestSent ||
              friendData.friendRequestReceived) && (
              <>
                {friendData?.isFriend === false && (
                  <Button
                    size="icon"
                    onClick={sendFriendRequest}
                    style={{ flexShrink: "0" }}
                  >
                    <PersonIcon />
                    {friendData?.friendRequestReceived ? (
                      <CheckIcon />
                    ) : (
                      <PlusIcon />
                    )}
                  </Button>
                )}
              </>
            )}
            {friendData?.isFriend === true && (
              <Button
                size="icon"
                onClick={handleRemoveFriend}
                style={{ flexShrink: "0" }}
                variant={removeFriendClicked ? "destructive" : "outline"}
              >
                <PersonIcon />
                <MinusIcon />
              </Button>
            )}
            {!friendData?.isFriend && (
              <>
                {friendData?.friendRequestSent && (
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={removeFriend}
                    style={{ flexShrink: "0" }}
                  >
                    <PersonIcon />
                    <EnvelopeClosedIcon />
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </CardHeader>
      {Content && (
        <CardContent
          className="p-4 pt-0"
          style={{
            paddingLeft: "calc(2.5rem + 1rem + 1ch)",
          }}
        >
          <Content user={data} />
        </CardContent>
      )}
    </Card>
  );
}
